import React, { forwardRef, useMemo, useState } from 'react';

import { Box, InputAdornment, TextField, useTheme } from '@mui/material';

import { CrossSmallIcon, EyeCloseIcon, EyeOpenIcon } from '@setup/svg-icons/svg-icons';
import { InputError, InputLabel, MuiButton } from '@shared/ui-kit';

import { textFilledStylesVariant } from '../MuiBaseTextFiled/utils/text-filled-styles-variant';

import type { InputLabelProps, OutlinedInputProps, SxProps } from '@mui/material';
import type { MuiButtonProps } from '@shared/ui-kit';

export type MuiBaseTextFiledInputVariant = 'outline' | 'text' | 'search' | 'withScroll';

type MuiBaseTextFiledProps = {
  labelId?: string;
  inputId?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  isReadOnly?: boolean;
  isFullWidth?: boolean;
  type?: string;
  isError?: boolean;
  inputVariant?: MuiBaseTextFiledInputVariant;
  label?: string;
  multiline?: boolean;
  placeholder?: string;
  helperText?: string;
  errorMessage?: string;
  size?: 'small' | 'medium';
  value?: string;
  onChange?: (val: React.ChangeEvent<HTMLInputElement>) => void;
  onClearValue?: () => void;
  isShowPasswordIcons?: boolean;
  endAdornment?: React.ReactNode;
  inputProps?: Partial<OutlinedInputProps>;
  withButton?: boolean;
  isToUpperCase?: boolean;
  buttonProps?: MuiButtonProps;
  inputLabelProps?: Partial<InputLabelProps>;
  isShowHint?: boolean;
  maxHintValue?: number;
  isCustomHasValue?: boolean;
  maxLength?: number;
  isClearable?: boolean;
  inputStyles?: SxProps;
  isBoldInputLabel?: boolean;
  isOffAutocomplete?: boolean;
  [x: string]: any;
};

export const MuiBaseTextFiled = forwardRef<HTMLHeadingElement, MuiBaseTextFiledProps>((props, ref) => {
  const {
    labelId,
    inputId,
    isDisabled,
    isError,
    isFullWidth = true,
    inputVariant = 'outline',
    isShowPasswordIcons = true,
    isRequired,
    isClearable,
    isReadOnly,
    multiline,
    isToUpperCase = false,
    isShowHint = false,
    maxHintValue = 72,
    label,
    value,
    placeholder,
    helperText,
    onChange,
    withButton,
    buttonProps,
    errorMessage,
    size,
    type = 'text',
    endAdornment,
    inputProps,
    inputLabelProps,
    isCustomHasValue = false,
    inputStyles,
    isBoldInputLabel = false,
    isOffAutocomplete = false,
    onClearValue,
    ...args
  } = props;
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState<boolean>(true);
  const handleClearValue = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (onChange) {
      const fakeEvent = {
        target: {
          value: '',
        },
        currentTarget: {
          value: '',
        },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(fakeEvent);
      onClearValue?.();
    }
  };
  const style = useMemo(() => {
    return textFilledStylesVariant({
      multiline,
      inputStyles,
      inputVariant,
      withButton,
      isReadOnly,
    });
  }, [inputStyles, inputVariant, multiline, withButton, isReadOnly]);

  const handleDoubleClick = (event: React.MouseEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    target?.select?.();
  };

  return (
    <Box
      sx={{
        width: '100%',
        '& .MuiOutlinedInput-root': {
          backgroundColor:
            (!!value && !isDisabled) || (isCustomHasValue && !isDisabled)
              ? theme.palette.case.neutral.n0
              : '',
        },
        ...style,
      }}
    >
      <InputLabel
        id={labelId}
        label={isToUpperCase ? label?.toUpperCase() : label}
        isShowHint={isShowHint}
        isRequired={isRequired}
        currentHintValue={value?.length}
        maxHintValue={maxHintValue}
        isBoldLabel={isBoldInputLabel}
      />
      <TextField
        {...args}
        id={inputId}
        label=""
        variant="outlined"
        value={value}
        inputRef={ref}
        multiline={multiline}
        autoComplete="new-password"
        type={type === 'password' ? (showPassword ? 'password' : 'text') : type}
        fullWidth={isFullWidth}
        disabled={isDisabled}
        required={isRequired}
        error={isError}
        placeholder={placeholder}
        onDoubleClick={handleDoubleClick}
        helperText={helperText}
        size={size}
        onChange={onChange}
        InputProps={{
          ...inputProps,
          readOnly: isReadOnly,
          classes: { root: 'input-root' },
          autoComplete: isOffAutocomplete ? 'off' : 'new-password',
          endAdornment: withButton ? (
            <InputAdornment position="end">
              <Box sx={{ zIndex: 10 }}>
                <MuiButton label={buttonProps?.label || ''} {...buttonProps} />
              </Box>
            </InputAdornment>
          ) : type === 'password' && isShowPasswordIcons ? (
            <Box
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeCloseIcon /> : <EyeOpenIcon />}
            </Box>
          ) : endAdornment ? (
            <InputAdornment position="end">{endAdornment}</InputAdornment>
          ) : isClearable && !!value ? (
            <InputAdornment position="end">
              <Box
                onClick={handleClearValue}
                onTouchStart={handleClearValue}
                sx={{
                  position: 'relative',
                  zIndex: '100',
                  width: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer !important',
                  '&:hover': {
                    opacity: '0.7',
                  },

                  color: `${
                    isDisabled ? theme.palette.case.neutral.n500 : theme.palette.case.neutral.n700
                  } !important`,
                }}
              >
                <CrossSmallIcon />
              </Box>
            </InputAdornment>
          ) : null,
        }}
        InputLabelProps={{
          shrink: true,
          ...inputLabelProps,
        }}
      />
      {errorMessage ? <InputError isShow={isError} message={errorMessage} /> : <></>}
    </Box>
  );
});
